import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import './BulkImport.css';
import { FiUpload, FiDownload, FiCheckCircle, FiXCircle, FiArrowLeft, FiClock, FiAlertTriangle, FiSearch, FiEye } from 'react-icons/fi';

const BulkImport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Tab state: 'import' | 'history' | 'skipped'
  const [activeTab, setActiveTab] = useState('import');

  // Skipped leads history state
  const [skippedLeads, setSkippedLeads] = useState([]);
  const [skippedLoading, setSkippedLoading] = useState(false);
  const [skippedSearchPhone, setSkippedSearchPhone] = useState('');
  const [skippedSearchEmail, setSkippedSearchEmail] = useState('');
  const [selectedSkippedLead, setSelectedSkippedLead] = useState(null);

  useEffect(() => {
    // Initial setup if needed
  }, []);

  // Fetch skipped leads history when tab is active
  useEffect(() => {
    if (activeTab === 'skipped') {
      fetchSkippedLeads();
    }
  }, [activeTab]);

  const fetchSkippedLeads = async () => {
    setSkippedLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (skippedSearchPhone) params.set('phone', skippedSearchPhone);
      if (skippedSearchEmail) params.set('email', skippedSearchEmail);
      params.set('limit', '100');

      const response = await axios.get(`${API_BASE_URL}/api/leads/skipped-leads-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkippedLeads(response.data);
    } catch (error) {
      console.error('Error fetching skipped leads:', error);
    } finally {
      setSkippedLoading(false);
    }
  };

  const handleSearchSkipped = (e) => {
    e.preventDefault();
    fetchSkippedLeads();
  };

  const viewExistingLead = (leadId) => {
    if (leadId) {
      navigate(`/leads/${leadId}`);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ DANGER: This will delete ALL leads. Are you sure?')) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/leads/delete-all-maintenance?key=fix_my_phones_please`);
      toast.success('✅ All leads deleted successfully!');
      window.location.reload();
    } catch (error) {
      toast.error('❌ Delete failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setDeleting(false);
    }
  };

  // Allow admin and all staff roles
  const canAccess = user?.role === 'ADMIN' ||
    user?.role === 'SALES_TEAM_HEAD' ||
    user?.role === 'SALES_TEAM' ||
    user?.role === 'PROCESSING' ||
    user?.role === 'STAFF';

  if (!canAccess) {
    return (
      <div className="bulk-import">
        <div className="bulk-import-error">
          <FiXCircle /> Access denied
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);

      // Preview CSV or Excel
      if (selectedFile.name.toLowerCase().endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target.result;
          const lines = text.split('\n').slice(0, 6); // First 5 rows + header
          setPreview(lines.join('\n'));
        };
        reader.readAsText(selectedFile);
      } else {
        // For Excel files, show a message
        setPreview('Excel file selected. Preview not available for Excel files.');
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('You are not logged in. Please log in again.');
        navigate('/login');
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/api/leads/bulk-import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          // DO NOT set Content-Type - axios will set it automatically with correct boundary for FormData
        },
      });

      setResult(response.data);
      setFile(null);
      setPreview(null);

      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Import error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      const errorMessage = error.response?.data?.error || error.message || 'Error importing leads';
      const errorDetails = error.response?.data?.details || error.response?.data?.message || '';
      const availableColumns = error.response?.data?.availableColumns || [];

      // Show full error details in console for debugging
      if (error.response?.data) {
        console.error('Full error response:', JSON.stringify(error.response.data, null, 2));
      }

      setResult({
        success: false,
        error: errorMessage,
        details: errorDetails,
        availableColumns: availableColumns,
        status: error.response?.status,
        fullError: error.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'name',
      'phone_number',
      'whatsapp_number',
      'email',
      'age',
      'occupation',
      'qualification',
      'year_of_experience',
      'country',
      'program',
      'status',
      'priority',
      'comment',
      'follow_up_date',
    ];

    const sampleRow = [
      'John Doe',
      '1234567890',
      '1234567890',
      'john@example.com',
      '30',
      'Engineer',
      'bachelors',
      '5',
      'australia',
      'gsm',
      'Unassigned',
      'warm',
      'Interested in product',
      '2026-02-01',
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(','),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'lead_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportLeads = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/leads/export/csv?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'xlsx' ? 'xlsx' : 'csv';
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${format.toUpperCase()} file downloaded successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error exporting leads. Please try again.');
    }
  };

  return (
    <div className="bulk-import">
      <div className="bulk-import-header">
        <button className="btn-back" onClick={() => navigate('/leads')}>
          <FiArrowLeft /> Back to Leads
        </button>
        <h1>Import & Export Leads</h1>
        <button className="btn-export-all" onClick={handleExportLeads}>
          <FiDownload /> Export Leads
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="import-tabs" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('import')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'import' ? '#D4AF37' : '#f3f4f6',
            color: activeTab === 'import' ? 'white' : '#374151',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'import' ? '600' : '400',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiUpload /> Import / Export
        </button>
        <button
          onClick={() => navigate('/leads?showHistory=true')}
          style={{
            padding: '12px 24px',
            background: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiClock /> Import History
        </button>
        <button
          onClick={() => setActiveTab('skipped')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'skipped' ? '#dc3545' : '#f3f4f6',
            color: activeTab === 'skipped' ? 'white' : '#374151',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'skipped' ? '600' : '400',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiAlertTriangle /> Skipped Leads History
        </button>
      </div>

      <div className="bulk-import-content">
        {activeTab === 'skipped' && (
          <div className="skipped-leads-section" style={{ padding: '20px' }}>
            <h2 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FiAlertTriangle color="#dc3545" /> Skipped Leads History
            </h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              View leads that were skipped during bulk imports due to duplicate phone numbers or email addresses.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearchSkipped} style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by phone..."
                value={skippedSearchPhone}
                onChange={(e) => setSkippedSearchPhone(e.target.value)}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  flex: '1',
                  minWidth: '200px'
                }}
              />
              <input
                type="text"
                placeholder="Search by email..."
                value={skippedSearchEmail}
                onChange={(e) => setSkippedSearchEmail(e.target.value)}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  flex: '1',
                  minWidth: '200px'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  background: '#D4AF37',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FiSearch /> Search
              </button>
            </form>

            {/* Skipped Leads Table */}
            {skippedLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
            ) : skippedLeads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No skipped leads found. Import some leads with duplicates to see them here.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Import Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>File</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Lead Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Phone</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Skip Reason</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Existing Lead</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skippedLeads.map((lead, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px' }}>
                          {new Date(lead.import_date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {lead.original_filename}
                        </td>
                        <td style={{ padding: '12px' }}>{lead.name || '-'}</td>
                        <td style={{ padding: '12px' }}>{lead.phone_number || '-'}</td>
                        <td style={{ padding: '12px' }}>{lead.email || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: lead.skip_reason?.includes('Phone') ? '#fff3cd' : '#f8d7da',
                            color: lead.skip_reason?.includes('Phone') ? '#856404' : '#721c24'
                          }}>
                            {lead.skip_reason}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {lead.existing_lead_id ? (
                            <div>
                              <div style={{ fontWeight: 500 }}>{lead.existing_lead_name}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{lead.existing_lead_status}</div>
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {lead.existing_lead_id && (
                            <button
                              onClick={() => viewExistingLead(lead.existing_lead_id)}
                              style={{
                                padding: '6px 12px',
                                background: '#D4AF37',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <FiEye /> View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'import' && (<>
        <div className="import-export-tabs">
          <div className="tab-section export-tab">
            <h2>📤 Export Leads</h2>
            <p>Export leads to CSV format for backup or use in Google Sheets</p>
            {user?.role !== 'ADMIN' && (
              <div className="staff-notice">
                <strong>Note:</strong> Staff members will only export leads assigned to them.
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button className="btn-export" onClick={() => handleExportLeads('csv')}>
                <FiDownload /> Export to CSV
              </button>
              <button className="btn-export" onClick={() => handleExportLeads('xlsx')}>
                <FiDownload /> Export to Excel
              </button>
            </div>
            <div className="export-info">
              <p><strong>Export includes:</strong></p>
              <ul>
                <li>All lead information (name, contact, program, status, etc.)</li>
                <li>{user?.role === 'ADMIN' ? 'All leads in the system' : 'Leads assigned to you'}</li>
                <li>Formatted for easy import into Google Sheets</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="import-instructions">
          <h2>📥 Import Leads</h2>
          {user?.role !== 'ADMIN' && (
            <div className="staff-notice">
              <strong>Note:</strong> Leads imported by staff members will be automatically assigned to you.
            </div>
          )}
          <h3>How to Import Leads</h3>
          <ol>
            <li>Prepare your data in Excel, Google Sheets, or CSV format</li>
            <li>Make sure your file has a header row with column names</li>
            <li>Required columns: <strong>name</strong> (or <strong>first_name</strong> + <strong>last_name</strong>) and <strong>phone</strong> (or <strong>phone_number</strong>)</li>
            <li>Download the template below to see the recommended format</li>
            <li>Upload your file (CSV or Excel) using the form below</li>
            <li>Review the import results</li>
          </ol>
          <p><strong>Supported formats:</strong> CSV (.csv), Excel (.xlsx, .xls)</p>
          <div style={{ marginTop: '15px', padding: '15px', background: '#e3f2fd', borderRadius: '8px', borderLeft: '4px solid #2196f3' }}>
            <h4 style={{ marginTop: 0, color: '#1976d2' }}>📱 Meta Ads (Facebook Ads) Support</h4>
            <p style={{ marginBottom: '10px' }}>This system automatically recognizes Meta Ads export format! Common Meta Ads columns are supported:</p>
            <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
              <li><strong>First Name</strong> / <strong>Last Name</strong> → Automatically combined into name</li>
              <li><strong>Phone Number</strong> → Automatically recognized</li>
              <li><strong>Email</strong> → Automatically recognized</li>
              <li><strong>Ad Name</strong>, <strong>Campaign Name</strong>, <strong>Form Name</strong> → Stored in lead source/comment</li>
              <li><strong>Created Time</strong> / <strong>Created Date</strong> → Used for follow-up date</li>
              <li><strong>Lead ID</strong> → Stored in comments for reference</li>
            </ul>
            <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>
              <strong>Tip:</strong> You can directly export leads from Meta Ads Manager and upload the Excel/CSV file - no manual formatting needed!
            </p>
          </div>
          <div className="template-download">
            <button className="btn-download-template" onClick={downloadTemplate}>
              <FiDownload /> Download CSV Template
            </button>
          </div>
        </div>

        <div className="import-form">
          <h2>Upload CSV File</h2>
          <div className="file-upload-area">
            <input
              id="csv-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="csv-file-input" className="file-label">
              <FiUpload /> {file ? file.name : 'Choose CSV or Excel File'}
            </label>
          </div>

          {preview && (
            <div className="preview-section">
              <h3>Preview (First 5 rows)</h3>
              <pre className="csv-preview">{preview}</pre>
            </div>
          )}

          <div className="import-actions">
            <button
              className="btn-import"
              onClick={handleImport}
              disabled={!file || loading}
            >
              {loading ? 'Importing...' : 'Import Leads'}
            </button>
          </div>
        </div>

        {result && (
          <div className={`import-result ${result.success ? 'success' : 'error'}`}>
            {result.success ? (
              <>
                <FiCheckCircle className="result-icon" />
                <h3>Import Successful!</h3>
                <div className="result-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Processed:</span>
                    <span className="stat-value">{result.total}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Successfully Created:</span>
                    <span className="stat-value success">{result.created}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Skipped (Duplicates or Missing Data):</span>
                    <span className="stat-value warning">{result.skipped}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Errors:</span>
                    <span className="stat-value error">{result.errors}</span>
                  </div>
                </div>
                {result.skipped > 0 && (
                  <div className="skip-notice" style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    <p>💡 Tip: {result.skipped} leads were skipped because their phone number or email already exists in the CRM, or they were missing required fields.</p>
                    <button
                      onClick={() => setActiveTab('skipped')}
                      style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      View Skipped Leads History →
                    </button>
                  </div>
                )}
                {result.skippedRows && result.skippedRows.length > 0 && (
                  <div className="skipped-details" style={{ marginTop: '20px' }}>
                    <h4 style={{ color: '#856404' }}>Skipped Rows (Duplicates):</h4>
                    <ul style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ffeeba', padding: '10px', borderRadius: '4px', background: '#fff' }}>
                      {result.skippedRows.map((skip, index) => (
                        <li key={index} style={{ fontSize: '12px', marginBottom: '4px' }}>
                          Row {skip.row}: {skip.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.errorRows && result.errorRows.length > 0 && (
                  <div className="error-details" style={{ marginTop: '20px' }}>
                    <h4 style={{ color: '#721c24' }}>Rows with Errors:</h4>
                    <ul style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #f5c6cb', padding: '10px', borderRadius: '4px', background: '#fff' }}>
                      {result.errorRows.map((error, index) => (
                        <li key={index} style={{ fontSize: '12px', marginBottom: '4px' }}>
                          Row {error.row}: {error.message || error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  className="btn-view-leads"
                  onClick={() => navigate('/leads')}
                >
                  View All Leads
                </button>
              </>
            ) : (
              <>
                <FiXCircle className="result-icon" />
                <h3>Import Failed</h3>
                <p className="error-message"><strong>Error:</strong> {result.error}</p>
                {result.status && (
                  <p className="error-status"><strong>Status Code:</strong> {result.status}</p>
                )}
                {result.details && (
                  <div className="error-details">
                    <p><strong>Details:</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {typeof result.details === 'string' ? result.details : JSON.stringify(result.details, null, 2)}
                    </pre>
                  </div>
                )}
                {result.availableColumns && result.availableColumns.length > 0 && (
                  <div className="error-details">
                    <p><strong>Available columns in your CSV:</strong></p>
                    <p style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                      {Array.isArray(result.availableColumns) ? result.availableColumns.join(', ') : result.availableColumns}
                    </p>
                    <p><strong>Required columns:</strong> name (or first_name + last_name), phone_number (or phone, mobile)</p>
                  </div>
                )}
                {result.fullError && (
                  <div className="error-details" style={{ marginTop: '10px' }}>
                    <p><strong>Full Error Response:</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                      {JSON.stringify(result.fullError, null, 2)}
                    </pre>
                  </div>
                )}
                <div style={{ marginTop: '15px', padding: '10px', background: '#fff3cd', borderRadius: '4px' }}>
                  <p><strong>💡 Troubleshooting Tips:</strong></p>
                  <ul style={{ textAlign: 'left', marginLeft: '20px' }}>
                    <li>Check the browser console (F12) for detailed error logs</li>
                    <li>Make sure your CSV has a header row with column names</li>
                    <li>Required columns: <code>name</code> (or <code>first_name</code> + <code>last_name</code>) and <code>phone</code> (or <code>phone_number</code>)</li>
                    <li>Download the template above to see the correct format</li>
                    <li>Make sure the file is saved as CSV (not Excel .xlsx)</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
};

export default BulkImport;
